/**
 * bandForScore — the ONE shared score → mastery-band function (v3.78.0).
 *
 * Who calls it: School View, the Grade Report, and the Dashboard (a pure
 * refactor — `Dashboard.vowelBands.test.js` + `bandForScore.test.js` pin the
 * identical label for every integer score). Results, NWF Results and the
 * NextPath mapper keep their own cuts until the BACKLOG "BAND CUT POINTS"
 * decision lands; when it does, change the table HERE and adopt it there.
 *
 * Per-family sources (owner decision 2026-09-14, item 1):
 *   - Letter tests (uppercase / lowercase / sounds, 26 items): the Dashboard's
 *     labelled cuts — Mastery ≥ 22 · Emerging 12–21 · Needs Support 0–11
 *     (`src/pages/Dashboard.js` bandIdFor, pre-3.78.0 lines 30–35). v3.85.0:
 *     the cuts are FRACTIONS of 26 (letterCutsFor) so a consonants-only run
 *     (21 letters, Sounds variants) bands at 18 / 10 — identical on 26.
 *     Results colors the same grid at ≥ 21 / ≥ 14 with no labels
 *     (`Results.js` getScoreColorClass — its own cuts, also scaled by the
 *     run's total since v3.85.0); the BAND CUT POINTS decision is still open.
 *   - Long / Short Vowels (5 items): `VOWEL_BANDS` wrapped, not copied
 *     (`src/constants/vowelTests.js:102`).
 *   - Nonsense Word Fluency (25 words by default; the doc's `totalLetters`
 *     is the real denominator): the Dashboard does not label NWF, so its
 *     Results page's own cuts carry the three labels — ≥ 21 / ≥ 14
 *     (`src/pages/BenchmarkScreenerResults.js:140-148`).
 *   - Phonological Awareness (4K) (`pa4k_*`, 5 items): `PA4K_BANDS` wrapped
 *     (`src/constants/pa4kItems.js:167`).
 *   - Phonemic Awareness screener (`pa_*`, 5 items): the two-band
 *     `PA_BANDS` wrapped — Proficient 4–5 / Developing 0–3
 *     (`src/constants/paScreenerItems.js:62`). Two bands, never three.
 *   - HFW / CVC (variable word counts, `totalWords`): the percentage cuts
 *     their Results pages color with — ≥ 80% / ≥ 50% — labelled
 *     (`src/pages/hfw/HFWResults.js:18-24`, `src/pages/cvc/CvcResults.js:17-23`).
 *   - PA Diagnostic (`pa_diagnostic`): cuts are tier DATA per sub-skill —
 *     `computeBand` from `src/pa-diagnostic/lib/computeBand.js` over the doc's
 *     `padTierId` / `padSubskillId`; partial docs have no band (the write
 *     path's rule). Pass the doc as `context.doc`.
 *   - 4K Readiness (`boy_*`): NO bands exist anywhere in the app
 *     (`src/utils/boyReport.js`); returns null and an empty legend — callers
 *     show the score.
 *
 * Contract: `''`, null, undefined, negative, NaN and the `-1` N/A sentinel all
 * return null (not administered ≠ zero). A score above `max` still bands
 * (a custom NWF list can exceed 25).
 */
import { VOWEL_BANDS, isVowelTestType } from '../constants/vowelTests';
import { PA4K_BANDS, PA4K_TEST_TYPES } from '../constants/pa4kItems';
import { PA_BANDS, PA_STEP_TYPES } from '../constants/paScreenerItems';
import { computeBand } from '../pa-diagnostic/lib/computeBand';
import { loadTier } from '../pa-diagnostic/lib/loadTier';
import { HFW_TEST_TYPE, CVC_TEST_TYPE, PAD_TEST_TYPE } from './reportCore';

export const LETTER_TEST_TYPES = ['uppercase', 'lowercase', 'sounds'];
export const NWF_TEST_TYPE = 'nonsense_word';
// The three synthetic / module test-type keys live in reportCore.js (v3.80.0,
// shared with the callable); re-exported so every importer is untouched.
export { HFW_TEST_TYPE, CVC_TEST_TYPE, PAD_TEST_TYPE };

export const BAND_LABELS = {
  mastery: 'Mastery',
  emerging: 'Emerging',
  needs_support: 'Needs Support',
  proficient: 'Proficient',
  developing: 'Developing',
};

// The Dashboard indicator palette (App.css .indicator-green/-yellow/-red).
export const BAND_COLORS = {
  mastery: '#28a745',
  emerging: '#ffc107',
  needs_support: '#dc3545',
  proficient: '#28a745',
  developing: '#ffc107',
};

/** Results-table cell classes (App.css .score-high/-mid/-low) per band key. */
export const BAND_CELL_CLASS = {
  mastery: 'score-high',
  emerging: 'score-mid',
  needs_support: 'score-low',
  proficient: 'score-high',
  developing: 'score-mid',
};

/** Dashboard dot classes (App.css .indicator-*) per band key. */
export const BAND_INDICATOR_CLASS = {
  mastery: 'indicator-green',
  emerging: 'indicator-yellow',
  needs_support: 'indicator-red',
  proficient: 'indicator-green',
  developing: 'indicator-yellow',
};

export const LETTER_MAX = 26;
const NWF_MAX = 25;

// Letter-test cuts as FRACTIONS of 26 (v3.85.0): Mastery from 22/26, Emerging
// from 12/26. On the 26-letter run the cuts are exactly 22 and 12 (a no-op);
// on a 21-letter consonants-only run they fall at 18 and 10. Integer maths —
// ceil(cut × n / 26) computed as floor((cut × n + 25) / 26) — so no float
// rounding can move a boundary.
const LETTER_MASTERY_CUT = 22;
const LETTER_EMERGING_CUT = 12;
const scaledCut = (cut, n) => Math.floor((cut * n + (LETTER_MAX - 1)) / LETTER_MAX);

/** The letter-test cut points for an n-item run: { masteryMin, emergingMin }. */
export function letterCutsFor(n) {
  const top = Number.isFinite(Number(n)) && Number(n) > 0 ? Math.floor(Number(n)) : LETTER_MAX;
  return { masteryMin: scaledCut(LETTER_MASTERY_CUT, top), emergingMin: scaledCut(LETTER_EMERGING_CUT, top), max: top };
}

/**
 * The scale a LEGEND is drawn on: the letter tests always read on the
 * 26-point scale (a consonants-only run's 21 gets a one-line note instead —
 * letterScaleNote); every other family legends on the doc's own max.
 */
export function legendMaxFor(testType, max) {
  return LETTER_TEST_TYPES.includes(testType) ? LETTER_MAX : max;
}

/**
 * The one-line legend note for letter-test results on a shorter run —
 * "Consonants only: 18 / 10 of 21" — or null when every result is /26.
 * `maxes` = the item totals seen (numbers; anything else ignored).
 */
export function letterScaleNote(maxes) {
  const shorter = [...new Set((maxes || []).map(Number).filter((m) => Number.isFinite(m) && m > 0 && m !== LETTER_MAX))].sort((a, b) => b - a);
  if (shorter.length === 0) return null;
  return shorter.map((m) => {
    const cuts = letterCutsFor(m);
    return `${m === 21 ? 'Consonants only' : `${m}-letter run`}: ${cuts.masteryMin} / ${cuts.emergingMin} of ${m}`;
  }).join(' · ');
}

const asScore = (score) => {
  if (score === '' || score === null || score === undefined) return null;
  const n = Number(score);
  if (!Number.isFinite(n) || n < 0) return null; // includes correctCount: -1 (N/A)
  return n;
};

const band = (key, min, max) => ({ key, label: BAND_LABELS[key], color: BAND_COLORS[key], min, max });

// Wrap a shared `{ id, label, min, max }` table (best band first).
const wrapTable = (table) =>
  [...table]
    .sort((a, b) => b.min - a.min)
    .map((b) => ({ key: b.id, label: b.label || BAND_LABELS[b.id], color: BAND_COLORS[b.id], min: b.min, max: b.max }));

const isPa4k = (t) => PA4K_TEST_TYPES.includes(t);
const isPaScreener = (t) => PA_STEP_TYPES.includes(t);
const isHfwCvc = (t) => t === HFW_TEST_TYPE || t === CVC_TEST_TYPE;
export const isBoyTestType = (t) => typeof t === 'string' && t.startsWith('boy_');

function padSubskill(doc) {
  if (!doc || !doc.padTierId || !doc.padSubskillId) return null;
  try {
    return loadTier(doc.padTierId).subskills.find((s) => s.id === doc.padSubskillId) || null;
  } catch {
    return null;
  }
}

const padLegend = (bands) => {
  if (!bands) return [];
  const rows = [
    ['mastery', bands.mastery],
    ['emerging', bands.emerging],
    ['needs_support', bands.needsSupport],
  ].filter(([, r]) => Array.isArray(r));
  return rows.map(([key, r]) => band(key, r[0], r[1])).sort((a, b) => b.min - a.min);
};

/**
 * Legend for a family: ordered best band first, each `{ key, label, color,
 * min, max }` in points. `max` is the family's item count (the doc's
 * totalLetters / totalWords) — it only matters for HFW / CVC (percent cuts)
 * and for the top band's upper bound. Empty array = the family has no bands.
 *
 * @param {string} testType
 * @param {number} [max]
 * @param {{ doc?: object }} [context]  PA-D needs the doc (tier + sub-skill).
 */
export function bandsForTestType(testType, max, context = {}) {
  if (LETTER_TEST_TYPES.includes(testType)) {
    // Cuts as fractions of 26 (letterCutsFor): 22 / 12 on a 26-letter run,
    // 18 / 10 on a 21-letter consonants-only run. The top band still absorbs
    // a score above `max` (a doc with no totalLetters bands on 26).
    const { masteryMin, emergingMin, max: top } = letterCutsFor(max);
    return [
      band('mastery', masteryMin, Math.max(top, masteryMin)),
      band('emerging', emergingMin, Math.max(masteryMin - 1, emergingMin)),
      band('needs_support', 0, Math.max(emergingMin - 1, 0)),
    ];
  }
  if (isVowelTestType(testType)) return wrapTable(VOWEL_BANDS);
  if (testType === NWF_TEST_TYPE) {
    const top = Number.isFinite(Number(max)) && Number(max) > 0 ? Number(max) : NWF_MAX;
    return [band('mastery', 21, Math.max(top, 21)), band('emerging', 14, 20), band('needs_support', 0, 13)];
  }
  if (isPa4k(testType)) return wrapTable(PA4K_BANDS);
  if (isPaScreener(testType)) return wrapTable(PA_BANDS);
  if (isHfwCvc(testType)) {
    const top = Number(max);
    if (!Number.isFinite(top) || top <= 0) return [];
    const masteryMin = Math.ceil(top * 0.8);
    const emergingMin = Math.ceil(top * 0.5);
    return [
      band('mastery', masteryMin, top),
      band('emerging', emergingMin, Math.max(masteryMin - 1, emergingMin)),
      band('needs_support', 0, Math.max(emergingMin - 1, 0)),
    ];
  }
  if (testType === PAD_TEST_TYPE) {
    const sub = padSubskill(context.doc);
    return padLegend(sub?.bands);
  }
  return []; // boy_* and anything unknown: no bands
}

/**
 * Band for one score.
 *
 * @param {string} testType  testType on the doc; `hfw` / `cvc` for the two
 *   collections whose docs carry none.
 * @param {number|string|null} score  correctCount (`-1` = N/A → null).
 * @param {number} [max]  the doc's totalLetters / totalWords (HFW/CVC need it).
 * @param {{ doc?: object }} [context]  the doc itself (PA-D tier lookup +
 *   partial-status rule).
 * @returns {{ key: string, label: string, color: string } | null}
 */
export function bandForScore(testType, score, max, context = {}) {
  const n = asScore(score);
  if (n === null) return null;
  if (testType === PAD_TEST_TYPE) {
    const doc = context.doc;
    if (doc && doc.padStatus === 'partial') return null;
    const sub = padSubskill(doc);
    const key = sub ? computeBand(n, sub.bands) : (doc && doc.band) || null;
    return key ? { key, label: BAND_LABELS[key], color: BAND_COLORS[key] } : null;
  }
  const legend = bandsForTestType(testType, max, context);
  if (legend.length === 0) return null;
  // Best band first: the first band whose lower bound the score reaches.
  const hit = legend.find((b) => n >= b.min);
  if (!hit) return null;
  return { key: hit.key, label: hit.label, color: hit.color };
}

/** Dashboard-style range text: "22-26", "5", "0-11". */
export function bandRangeText(b) {
  return b.min === b.max ? `${b.min}` : `${b.min}-${b.max}`;
}

/**
 * Explicit cut-point text for a legend row — "≥22", "12–21", "<12" — so a
 * reader who sees a 21 colored green on Results and Emerging here is not
 * misled (owner decision item 1).
 */
export function bandCutText(b, legend) {
  const isTop = legend[0] && legend[0].key === b.key;
  const isBottom = legend[legend.length - 1] && legend[legend.length - 1].key === b.key;
  if (isTop) return `≥${b.min}`;
  if (isBottom) return `<${b.max + 1}`;
  return b.min === b.max ? `${b.min}` : `${b.min}–${b.max}`;
}

/**
 * The per-family table, for docs and tests (docs/requirements/school-view.md).
 * Order = the sidebar's family order.
 */
export const BAND_FAMILY_TABLE = [
  { family: 'Phonemic Awareness screener (pa_*)', max: 5, cuts: 'Proficient 4–5 · Developing 0–3', source: 'src/constants/paScreenerItems.js:62 (PA_BANDS, wrapped)' },
  { family: 'Phonological Awareness (4K) (pa4k_*)', max: 5, cuts: 'Mastery 4–5 · Emerging 2–3 · Needs Support 0–1', source: 'src/constants/pa4kItems.js:167 (PA4K_BANDS, wrapped)' },
  { family: 'PA Diagnostic (pa_diagnostic)', max: 'per tier', cuts: 'tier data per sub-skill (standard 4–5 / 2–3 / 0–1; Rhyme Detection 5 / 3–4 / 0–2); partial docs unbanded', source: 'src/pa-diagnostic/lib/computeBand.js + data/pa-*.json' },
  { family: '4K Readiness (boy_*)', max: 'per check', cuts: 'no bands — score only', source: 'src/utils/boyReport.js (no band derivation exists)' },
  { family: 'Letter/Sound uppercase / lowercase / sounds', max: 26, cuts: 'Mastery ≥22 · Emerging 12–21 · Needs Support 0–11 (fractions of 26: a 21-letter consonants-only run cuts at 18 / 10)', source: 'src/pages/Dashboard.js bandIdFor (pre-3.78.0 :30-35); Results.js getScoreColorClass colors at ≥21 / ≥14 (scaled the same way) — its own cuts' },
  { family: 'Long / Short Vowels', max: 5, cuts: 'Mastery 5 · Emerging 3–4 · Needs Support 0–2', source: 'src/constants/vowelTests.js:102 (VOWEL_BANDS, wrapped)' },
  { family: 'High Frequency Words (hfw_assessments)', max: 'totalWords', cuts: 'Mastery ≥80% · Emerging ≥50% · Needs Support <50%', source: 'src/pages/hfw/HFWResults.js:18-24 (colors only; labels added here)' },
  { family: 'CVC Words (cvc_assessments)', max: 'totalWords', cuts: 'Mastery ≥80% · Emerging ≥50% · Needs Support <50%', source: 'src/pages/cvc/CvcResults.js:17-23 (colors only; labels added here)' },
  { family: 'Nonsense Word Fluency (nonsense_word)', max: 25, cuts: 'Mastery ≥21 · Emerging 14–20 · Needs Support 0–13', source: 'src/pages/BenchmarkScreenerResults.js:140-148 (colors only; labels added here; NextPath untouched)' },
];
